/**
 * Date utility functions for handling Unix timestamps
 */

import { format, parseISO } from 'date-fns';

/**
 * Converts Unix timestamp to Date object
 */
export function unixToDate(timestamp: number): Date {
  return new Date(timestamp * 1000);
}

/**
 * Converts Unix timestamp to ISO string
 */
export function unixToISOString(timestamp: number): string {
  return unixToDate(timestamp).toISOString();
}

/**
 * Formats Unix timestamp for display
 */
export function formatUnixTimestamp(timestamp: number, formatStr: string = 'MMM dd, yyyy HH:mm'): string {
  return format(unixToDate(timestamp), formatStr);
}

/**
 * Checks if Unix timestamp represents an overdue date
 */
export function isUnixTimestampOverdue(timestamp: number): boolean {
  const now = Math.floor(Date.now() / 1000);
  return timestamp < now;
}

/**
 * Formats ETA from Unix timestamp with fallback
 * @deprecated Use getJobEtaDisplay from jobUtils.ts instead for comprehensive job ETA handling
 */
export function formatEta(etaNum: number | undefined): string {
  if (!etaNum) return 'No ETA';
  
  // Check if etaNum is at max value (used for jobs without real ETAs)
  const MAX_ETA_NUM = Number.MAX_SAFE_INTEGER;
  if (etaNum === MAX_ETA_NUM) return 'N/A';
  
  try {
    return formatUnixTimestamp(etaNum);
  } catch {
    return 'N/A';
  }
}

/**
 * Formats date for display - handles both ISO strings and Unix timestamps
 */
export function formatDate(value: string | number | undefined, formatStr: string = 'MMM dd, yyyy HH:mm'): string {
  if (!value) return 'N/A';
  
  try {
    if (typeof value === 'number') {
      // Unix timestamp
      return formatUnixTimestamp(value);
    } else {
      // ISO string
      return format(parseISO(value), formatStr);
    }
  } catch {
    return 'N/A';
  }
}
